import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Award, Clock, Shield } from "lucide-react";
import dalepLogo from "@/assets/dalep-logo-agree.png";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const About = () => {
  useScrollAnimation();
  const stats = [
    { value: "15+", label: "Ans d'expérience" },
    { value: "500+", label: "Chantiers" },
    { value: "100%", label: "Satisfaits" },
  ];

  const advantages = [
    {
      icon: Award,
      title: "Expertise",
      text: "+15 ans d'expérience",
    },
    {
      icon: Clock,
      title: "Devis Gratuit",
      text: "Sous 24h",
    },
    {
      icon: CheckCircle,
      title: "Rapide",
      text: "Intervention urgence",
    },
    {
      icon: Shield,
      title: "Garantie",
      text: "Décennale",
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-background-gray to-background relative overflow-hidden">
      {/* Decorative gradient orbs with float animation */}
      <div className="absolute top-10 right-10 w-80 h-80 bg-accent/10 rounded-full blur-3xl animate-float"></div>
      <div className="absolute bottom-10 left-10 w-80 h-80 bg-secondary/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* About Text */}
        <div className="text-center max-w-4xl mx-auto mb-16" data-animate>
          <div className="inline-block px-6 py-2 bg-primary/10 text-primary font-semibold rounded-full mb-6 text-sm uppercase tracking-wider animate-bounce-in">
            Qui sommes-nous
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold mb-8 text-primary animate-fade-in">
            À Propos de Jacky Couverture
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed">
            Jacky Couverture, artisan couvreur basé à Treillières. +15 ans d'expérience en couverture, 
            zinguerie et rénovation. Spécialiste tuile et ardoise. Intervention rapide, devis gratuit, 
            garantie décennale.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {stats.map((stat, index) => (
            <div 
              key={index} 
              data-animate
              data-animate-delay={index * 100}
              className="text-center p-8 rounded-2xl bg-card shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 group hover-glow card-hover"
            >
              <div className="text-5xl md:text-6xl font-extrabold gradient-text mb-3 group-hover:scale-125 transition-transform duration-500">
                {stat.value}
              </div>
              <div className="text-muted-foreground font-semibold text-lg">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Logo Dalep */}
        <div className="text-center mb-20" data-animate>
          <p className="text-muted-foreground mb-6 text-lg font-medium animate-fade-in">Nous utilisons les produits professionnels</p>
          <img 
            src={dalepLogo} 
            alt="Logo Dalep - Produits professionnels agréés" 
            className="h-32 mx-auto animate-scale-in hover-scale" 
            width="128" 
            height="128" 
            loading="lazy"
            style={{ maxWidth: '128px', maxHeight: '128px', objectFit: 'contain' }}
          />
        </div>

        {/* Advantages */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {advantages.map((advantage, index) => (
            <Card 
              key={index} 
              data-animate
              data-animate-delay={index * 100}
              className="border-0 shadow-lg hover:shadow-2xl bg-card transition-all duration-500 hover:-translate-y-3 group overflow-hidden hover-glow card-hover"
            >
              <CardContent className="p-8 text-center relative">
                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-accent/0 to-accent/0 group-hover:from-accent/10 group-hover:to-secondary/10 transition-all duration-500"></div>
                
                <div className="relative z-10">
                  <div className="mx-auto mb-6 w-16 h-16 rounded-2xl bg-gradient-to-br from-accent/20 to-secondary/20 flex items-center justify-center transition-all duration-500 group-hover:scale-125 group-hover:rotate-12">
                    <advantage.icon className="h-8 w-8 text-accent transition-transform duration-500 group-hover:scale-110" />
                  </div>
                  <h3 className="font-bold text-xl mb-3 text-primary group-hover:text-accent transition-colors duration-300">
                    {advantage.title}
                  </h3>
                  <p className="text-muted-foreground font-medium">
                    {advantage.text}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;